import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import FieldHelp from '@/components/help/FieldHelp';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import helpContent from '@/help/helpContent';
import sensitiveActions from '@/help/sensitiveActions';
import { Users, Plus, ChevronRight, Archive, UserPlus, UserMinus } from 'lucide-react';

export default function AudienceBuilder() {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState({ key: '', name: '', description: '' });
  const [detail, setDetail] = useState(null);
  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [addContactId, setAddContactId] = useState('');
  const [consentSummary, setConsentSummary] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get('/tenant/audiences'); setAudiences(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    const [a, m, cs] = await Promise.all([
      api.get(`/tenant/audiences/${id}`), api.get(`/tenant/audiences/${id}/members`),
      api.get(`/tenant/audiences/${id}/consent-summary`),
    ]);
    setDetail(a.data); setMembers(m.data || []); setConsentSummary(cs.data || {}); setTab('detail');
    try { const ct = await api.get('/tenant/contacts?page_size=50'); setContacts(ct.data || []); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleSave = async () => {
    try { await api.post('/tenant/audiences', form); toast.success('Audiencia creada'); setTab('list'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAddMember = async () => {
    if (!addContactId) return;
    try { await api.post(`/tenant/audiences/${detail.id}/members`, { contact_id: addContactId }); toast.success('Miembro agregado'); loadDetail(detail.id); setAddContactId(''); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleRemoveMember = async (contactId) => {
    try { await api.delete(`/tenant/audiences/${detail.id}/members/${contactId}`); toast.success('Miembro removido'); loadDetail(detail.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="audience-builder-page">
      <SectionHelp module="audiences" title={helpContent.audiences?.title || 'Audiencias'} description={helpContent.audiences?.description} whatItDoesNot={helpContent.audiences?.whatItDoesNot} />
      <VerticalExampleBox module="audiences" />

      {tab === 'list' && (<>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#64748B]">{audiences.length} audiencia{audiences.length !== 1 ? 's' : ''}</p>
          <Button size="sm" onClick={() => { setForm({ key: '', name: '', description: '' }); setTab('form'); }} data-testid="audience-create-btn"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
        </div>
        {audiences.length === 0 ? <EmptyStateGuide module="audiences" message="Sin audiencias. Crea una audiencia y agrega contactos para vincularla a campañas." ctaLabel="Crear audiencia" onCta={() => setTab('form')} /> : (
          <div className="space-y-2" data-testid="audience-list">
            {audiences.map(a => (
              <div key={a.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-[#94A3B8]" onClick={() => loadDetail(a.id)} data-testid={`audience-${a.id}`}>
                <Users className="w-6 h-6 text-[#94A3B8]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0F172A]">{a.name}</p>
                  <p className="text-xs text-[#94A3B8]">{a.key} · {a.estimated_count} miembros</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-emerald-50 text-emerald-700' : a.status === 'archived' ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
              </div>
            ))}
          </div>
        )}
      </>)}

      {tab === 'form' && (
        <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="audience-form">
          <h3 className="text-sm font-bold">Nueva audiencia</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldHelp label={helpContent.audiences.fields.key.label} help={helpContent.audiences.fields.key.help} htmlFor="audience-key">
              <Input value={form.key} onChange={e => setForm({...form, key: e.target.value})} data-testid="audience-key" id="audience-key" />
            </FieldHelp>
            <FieldHelp label="Nombre" help="Nombre descriptivo de la audiencia." htmlFor="audience-name">
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="audience-name" id="audience-name" />
            </FieldHelp>
            <div className="md:col-span-2">
              <FieldHelp label="Descripción" help="Descripción opcional para identificar la audiencia." htmlFor="audience-desc">
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="audience-desc" id="audience-desc" />
              </FieldHelp>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setTab('list')}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} data-testid="audience-save-btn">Crear</Button>
          </div>
        </div>
      )}

      {tab === 'detail' && detail && (
        <div className="space-y-4" data-testid="audience-detail">
          <div className="flex items-center gap-2">
            <button onClick={() => { setTab('list'); setDetail(null); }} className="text-xs text-[#64748B]">← Volver</button>
            <h3 className="text-sm font-bold flex-1">{detail.name}</h3>
            <span className="text-xs text-[#64748B]">{detail.estimated_count} miembros</span>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-xs font-bold mb-2">Consent Summary</h4>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-gray-50 rounded p-2">Total: {consentSummary.total || 0}</div>
              {Object.entries(consentSummary).filter(([k]) => k !== 'total').map(([k, v]) => (
                <div key={k} className="bg-gray-50 rounded p-2 text-[10px]">{k}: {v}</div>
              ))}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-4 space-y-2">
            <h4 className="text-xs font-bold">Miembros ({members.length})</h4>
            {members.map(m => (
              <div key={m.id} className="flex items-center gap-2 text-xs border-b pb-1">
                <span className="flex-1 text-[#475569]">{m.contact_id}</span>
                <button onClick={() => handleRemoveMember(m.contact_id)} className="text-red-500 hover:underline flex items-center gap-1"><UserMinus className="w-3 h-3" /> Remover</button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <select value={addContactId} onChange={e => setAddContactId(e.target.value)} className="flex-1 border rounded px-2 py-1 text-xs" data-testid="audience-add-contact-select">
                <option value="">Seleccionar contacto...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.display_name} ({c.primary_email || c.primary_phone})</option>)}
              </select>
              <Button size="sm" onClick={handleAddMember} data-testid="audience-add-member-btn"><UserPlus className="w-3.5 h-3.5 mr-1" /> Agregar</Button>
            </div>
          </div>
          {detail.status !== 'archived' && (
            <SensitiveActionHelp action={sensitiveActions.audience_archive} onConfirm={async () => { try { await api.patch(`/tenant/audiences/${detail.id}/archive`); toast.success('Archivada'); setTab('list'); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }}}>
              <Button size="sm" variant="outline" data-testid="audience-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" /> Archivar</Button>
            </SensitiveActionHelp>
          )}
        </div>
      )}
    </div>
  );
}
